import { useState } from 'react'
import { Alert, Button, Card, Field, Note, SecurityNote, ShieldIcon } from '@esign/ui'
import { FIELD_LABELS, errorText } from '../fields.js'
import { isSignatureReady } from '../signing.js'
import type { StepProps } from '../types.js'

const SIGN_LABEL: Record<'otp' | 'ca' | 'click', string> = {
  otp: 'Nhập mã OTP để ký',
  ca: 'Ký bằng chữ ký số',
  click: 'Ký hợp đồng',
}

export function SignStep({ step, context, error, busy, submit }: StepProps<'sign'>) {
  const [otp, setOtp] = useState('')
  const [accepted, setAccepted] = useState(false)
  const { method } = step.config
  const canSign = isSignatureReady(method, otp, accepted)

  return (
    <>
      <Card title={step.label}>
        {error && <Alert>{errorText(error.code)}</Alert>}

        <ContractPreview
          templateId={context.legal.template_id}
          version={context.legal.template_version}
          partnerName={context.partner.name}
          subject={context.subject}
        />

        {method === 'otp' && (
          <Field
            name="otp"
            label={FIELD_LABELS['otp']}
            value={otp}
            icon={<ShieldIcon />}
            hint="Bản demo dùng mã 123456"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            onValueChange={(value) => setOtp(value.replace(/\D/g, '').slice(0, 6))}
          />
        )}

        {method === 'ca' && step.config.provider && (
          <Note>Bạn sẽ được chuyển sang {step.config.provider} để ký bằng chữ ký số cá nhân.</Note>
        )}

        <SecurityNote />

        <label className="contract-consent">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(event) => setAccepted(event.target.checked)}
          />
          <span>Tôi đã đọc bản xem trước và đồng ý ký hợp đồng điện tử này.</span>
        </label>

        <Button disabled={busy || !canSign} onClick={() => submit(method === 'otp' ? { otp } : {})}>
          {busy ? 'Đang ký…' : SIGN_LABEL[method]}
        </Button>
      </Card>
    </>
  )
}

const ContractPreview = ({
  templateId,
  version,
  partnerName,
  subject,
}: {
  templateId: string
  version: number
  partnerName: string
  subject: Record<string, string | string[]>
}) => (
  <article className="contract-preview" aria-label="Bản xem trước hợp đồng">
    <header className="contract-preview__header">
      <p className="contract-preview__eyebrow">Bản xem trước hợp đồng</p>
      <h3>Hợp đồng dịch vụ điện tử</h3>
      <p>{templateId}, phiên bản {version}</p>
    </header>

    <dl className="contract-preview__parties">
      <div>
        <dt>Đối tác</dt>
        <dd>{partnerName}</dd>
      </div>
      <div>
        <dt>Người ký</dt>
        <dd>{String(subject['full_name'] ?? 'Chưa có thông tin')}</dd>
      </div>
      <div>
        <dt>Số CCCD</dt>
        <dd>{maskIdentity(String(subject['id_no'] ?? ''))}</dd>
      </div>
      <div>
        <dt>Mã số thuế</dt>
        <dd>{String(subject['tax_code'] ?? 'Chưa có thông tin')}</dd>
      </div>
    </dl>

    <p className="contract-preview__notice">
      Vui lòng kiểm tra thông tin trước khi ký. Bản hợp đồng hoàn tất sẽ được lưu cùng mã phiên ký.
    </p>
  </article>
)

const maskIdentity = (value: string): string =>
  value.length > 4 ? `${'*'.repeat(value.length - 4)}${value.slice(-4)}` : value || 'Chưa có thông tin'
